// useAvailability — status for a set of users, and useMyAvailability for the caller.
// Same shape as usePresence (which it supersedes wherever a dot is shown): one
// batched request for the whole set, refreshed on a ~60s interval and whenever the
// window regains focus. Nothing polls while the tab is hidden.
//
// Availability changes rarely, so 60s is courteous rather than chatty. When the
// caller changes their own status we nudge every mounted hook immediately instead of
// waiting for the next tick.
import { useCallback, useEffect, useState } from 'react';
import { conversationsApi } from '../api/conversationsApi';
import { indexAvailability } from '../availability';
import type { Availability, AvailabilityInput, AvailabilityOption } from '../types';

const REFRESH_MS = 60000;

// Module-level fan-out: "my status changed, everyone re-read".
const listeners = new Set<() => void>();
export function notifyAvailabilityChanged(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a stale listener must never break the others */
    }
  });
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

const hidden = () => typeof document !== 'undefined' && document.hidden;

/** Availability by user_id for the given ids (absent ids simply have no entry). */
export function useAvailability(userIds: string[]): Record<string, Availability> {
  const [byUser, setByUser] = useState<Record<string, Availability>>({});
  // Sorted + joined so a re-ordered array doesn't refetch.
  const key = Array.from(new Set(userIds.filter(Boolean))).sort().join(',');

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setByUser({});
      return;
    }
    let stopped = false;
    const tick = async (force = false) => {
      if (!force && hidden()) return;
      try {
        const items = await conversationsApi.getAvailability(ids);
        if (!stopped) setByUser(indexAvailability(items));
      } catch {
        /* transient — keep the last known statuses rather than blanking the UI */
      }
    };
    void tick(true);
    const timer = window.setInterval(() => void tick(), REFRESH_MS);
    const onFocus = () => void tick(true);
    window.addEventListener('focus', onFocus);
    const unsubscribe = subscribe(() => void tick(true));
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      unsubscribe();
    };
  }, [key]);

  return byUser;
}

export interface MyAvailabilityState {
  availability?: Availability;
  options: AvailabilityOption[];
  /** The server's own explanation of how status works here. */
  note: string;
  loading: boolean;
  saving: boolean;
  error: string;
  refresh: () => Promise<void>;
  save: (input: AvailabilityInput) => Promise<void>;
  clear: () => Promise<void>;
}

/** My status + the options I may pick. Also refreshed on focus and every ~60s, so a
 *  leave that starts (or a status that expires) shows up without a reload. */
export function useMyAvailability(enabled = true): MyAvailabilityState {
  const [availability, setAvailability] = useState<Availability | undefined>();
  const [options, setOptions] = useState<AvailabilityOption[]>([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const mine = await conversationsApi.getMyAvailability();
      setAvailability(mine.availability);
      setOptions(mine.options);
      setNote(mine.note);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unavailable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    const tick = (force = false) => {
      if (!force && hidden()) return;
      void load().then(() => {
        if (stopped) return;
      });
    };
    tick(true);
    const timer = window.setInterval(() => tick(), REFRESH_MS);
    const onFocus = () => tick(true);
    window.addEventListener('focus', onFocus);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, load]);

  const save = useCallback(
    async (input: AvailabilityInput) => {
      setSaving(true);
      try {
        const next = await conversationsApi.setMyAvailability(input);
        setAvailability(next);
        notifyAvailabilityChanged();
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const clear = useCallback(async () => {
    setSaving(true);
    try {
      const next = await conversationsApi.clearMyAvailability();
      setAvailability(next);
      notifyAvailabilityChanged();
    } finally {
      setSaving(false);
    }
  }, []);

  return { availability, options, note, loading, saving, error, refresh: load, save, clear };
}
